/**
 * AdventureFileDetailsDialog Component
 * Modal dialog showing file preview, metadata, and edit options
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Grid,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  Close,
  ContentCopy,
  Download,
  Delete,
  Image as ImageIcon,
  VideoFile,
  AudioFile,
  Description,
  InsertDriveFile,
} from '@mui/icons-material'
import type { AdventureMediaFileDto } from '@api/types/adventureFiles'
import { formatFileSize, getFileTypeIcon, formatDuration } from '@api/types/adventureFiles'
import { useUpdateAdventureFile } from '../../../hooks/useAdventureFiles'

interface AdventureFileDetailsDialogProps {
  open: boolean
  onClose: () => void
  file: AdventureMediaFileDto | null
  adventureId: string | null
  onDelete?: () => void
  canEdit?: boolean
}

function FileTypeIcon({ mimeType }: { mimeType: string }) {
  const type = getFileTypeIcon(mimeType)
  const iconProps = { sx: { fontSize: 64, color: 'text.secondary' } }

  switch (type) {
    case 'image':
      return <ImageIcon {...iconProps} />
    case 'video':
      return <VideoFile {...iconProps} />
    case 'audio':
      return <AudioFile {...iconProps} />
    case 'document':
      return <Description {...iconProps} />
    default:
      return <InsertDriveFile {...iconProps} />
  }
}

function FilePreview({ file }: { file: AdventureMediaFileDto }) {
  const isImage = file.mimeType.startsWith('image/')
  const isVideo = file.mimeType.startsWith('video/')
  const isAudio = file.mimeType.startsWith('audio/')

  if (isImage) {
    return (
      <Box
        component="img"
        src={file.fileUrl}
        alt={file.displayName || file.fileName}
        sx={{
          maxWidth: '100%',
          maxHeight: 400,
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto',
          borderRadius: 1,
        }}
      />
    )
  }

  if (isVideo) {
    return (
      <Box
        component="video"
        src={file.fileUrl}
        controls
        sx={{
          maxWidth: '100%',
          maxHeight: 400,
          display: 'block',
          margin: '0 auto',
          borderRadius: 1,
        }}
      />
    )
  }

  if (isAudio) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <FileTypeIcon mimeType={file.mimeType} />
        <Box
          component="audio"
          src={file.fileUrl}
          controls
          sx={{ width: '100%', mt: 2 }}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <FileTypeIcon mimeType={file.mimeType} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Preview not available for this file type
      </Typography>
    </Box>
  )
}

export function AdventureFileDetailsDialog({
  open,
  onClose,
  file,
  adventureId,
  onDelete,
  canEdit = true,
}: AdventureFileDetailsDialogProps) {
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  const updateFile = useUpdateAdventureFile(adventureId)

  // Reset form when file changes
  useEffect(() => {
    if (file) {
      setDisplayName(file.displayName || '')
      setDescription(file.description || '')
      setIsEditing(false)
    }
  }, [file])

  const handleCopyUrl = async () => {
    if (file) {
      await navigator.clipboard.writeText(file.fileUrl)
      setCopySuccess(true)
    }
  }

  const handleDownload = () => {
    if (file) {
      const link = document.createElement('a')
      link.href = file.fileUrl
      link.download = file.fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleSave = async () => {
    if (!file) return

    try {
      await updateFile.mutateAsync({
        fileId: file.id,
        data: {
          displayName: displayName || undefined,
          description: description || undefined,
        },
      })
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update file:', error)
    }
  }

  const handleCancel = () => {
    if (file) {
      setDisplayName(file.displayName || '')
      setDescription(file.description || '')
    }
    setIsEditing(false)
  }

  if (!file) return null

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
            {file.displayName || file.fileName}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Preview */}
          <Box sx={{ mb: 3 }}>
            <FilePreview file={file} />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* URL with copy button */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              File URL
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={file.fileUrl}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <Tooltip title="Copy URL">
                <IconButton onClick={handleCopyUrl}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Editable fields */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Display Name
              </Typography>
              <TextField
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value)
                  setIsEditing(true)
                }}
                placeholder={file.fileName}
                size="small"
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <TextField
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value)
                  setIsEditing(true)
                }}
                placeholder="Add a description..."
                size="small"
                fullWidth
                multiline
                rows={2}
                disabled={!canEdit}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Metadata */}
          <Typography variant="subtitle2" gutterBottom>
            File Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                File Name
              </Typography>
              <Typography variant="body2">{file.fileName}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                File Size
              </Typography>
              <Typography variant="body2">{formatFileSize(file.fileSize)}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body2">{file.mimeType}</Typography>
            </Grid>
            {file.width && file.height && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Dimensions
                </Typography>
                <Typography variant="body2">{file.width} x {file.height}</Typography>
              </Grid>
            )}
            {file.duration && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body2">{formatDuration(file.duration)}</Typography>
              </Grid>
            )}
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Uploaded
              </Typography>
              <Typography variant="body2">
                {new Date(file.createdDate).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Usage Count
              </Typography>
              <Typography variant="body2">{file.usageCount}</Typography>
            </Grid>
          </Grid>

          {updateFile.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to update file. Please try again.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            <Button
              startIcon={<Download />}
              onClick={handleDownload}
              variant="outlined"
            >
              Download
            </Button>
            {canEdit && onDelete && (
              <Button
                startIcon={<Delete />}
                onClick={onDelete}
                color="error"
                variant="outlined"
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditing && (
              <>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={updateFile.isPending}
                >
                  {updateFile.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Save'
                  )}
                </Button>
              </>
            )}
            {!isEditing && (
              <Button onClick={onClose}>Close</Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="URL copied to clipboard"
      />
    </>
  )
}
